import React, { useEffect, useRef, useId, useState } from 'react';
import mermaid from 'mermaid';
import { X, ZoomIn, ZoomOut, CircuitBoard,Download, Maximize2, Minimize2 } from 'lucide-react';
import { Canvg } from 'canvg';

interface ERDPreviewModalProps {
    chart: string;
    onClose: () => void;
}

const ERDPreviewModal: React.FC<ERDPreviewModalProps> = ({ chart, onClose }) => {
    const renderRef = useRef<HTMLDivElement>(null);
    const uniqueId = useId().replace(/:/g, '_');
    const [scale, setScale] = useState(1);
    const modalRef = useRef<HTMLDivElement>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isRendered, setIsRendered] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            renderChart();
        }, 100); // allow modal to paint
        return () => clearTimeout(timeout);
    }, [chart]);

    const renderChart = async () => {
        try {
            mermaid.initialize({ startOnLoad: false, theme: 'dark' });
            const { svg } = await mermaid.render(uniqueId, chart);
            if (renderRef.current) {
                renderRef.current.innerHTML = '';
                const wrapper = document.createElement('div');
                wrapper.innerHTML = svg;
                wrapper.id = 'erd-diagram-wrapper';
                wrapper.style.transform = `scale(${scale})`;
                wrapper.style.transformOrigin = 'top left';
                renderRef.current.appendChild(wrapper);
                setIsRendered(true);
            }
        } catch (err) {
            console.error(err);
            renderRef.current!.innerHTML =
                '<p class="text-red-400 text-sm">⚠️ Failed to render ERD. Check syntax.</p>';
        }
    };

    useEffect(() => {
        if (isRendered) {
            const wrapper = document.getElementById('erd-diagram-wrapper');
            if (wrapper) {
                wrapper.style.transform = `scale(${scale})`;
                wrapper.style.transformOrigin = 'top left';
            }
        }
    }, [scale]);

    const toggleFullScreen = async () => {
        if (!modalRef.current) return;
        if (!document.fullscreenElement) {
            await modalRef.current.requestFullscreen();
            setIsFullScreen(true);
        } else {
            await document.exitFullscreen();
            setIsFullScreen(false);
        }
    };

    const downloadSvgAsPng = async () => {
        const svgElement = renderRef.current?.querySelector('svg');
        if (!svgElement) return;
        const svgText = svgElement.outerHTML;

        const clone = svgElement.cloneNode(true) as SVGSVGElement;
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        document.body.appendChild(clone);

        const bbox = clone.getBoundingClientRect();
        const width = bbox.width || 1000;
        const height = bbox.height || 600;

        document.body.removeChild(clone);

        const scaleFactor = 4;
        const canvas = document.createElement('canvas');
        canvas.width = width * scaleFactor;
        canvas.height = height * scaleFactor;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.scale(scaleFactor, scaleFactor);

        const v = await Canvg.from(ctx, svgText);
        await v.render();

        const pngUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = 'erd-diagram.png';
        link.href = pngUrl;
        link.click();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-6 overflow-y-auto">
            <div ref={modalRef} className="bg-neutral-900 max-w-6xl mx-auto rounded-lg shadow-lg border border-neutral-800">
                <div className="flex justify-between items-center border-b border-neutral-800 px-6 py-4">
                    <h2 className="text-white text-xl font-bold flex items-center gap-3 tracking-tight">
                        <CircuitBoard className="w-5 h-5 text-indigo-400" />
                        <span className="inline-block bg-indigo-600 text-white text-xs px-2 py-1 rounded-md shadow-sm">
                            ERD
                        </span>
                        <span className="text-gray-100">Entity Relationship Diagram</span>
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4">
                    {/* Controls */}
                    <div className="flex items-center gap-3 mb-4 text-sm text-white flex-wrap">
                        <button onClick={() => setScale(prev => Math.max(0.5, prev - 0.1))} className="flex items-center gap-1 px-3 py-1 bg-neutral-700 rounded hover:bg-neutral-600">
                            <ZoomOut className="w-4 h-4" />
                            Zoom Out
                        </button>
                        <button onClick={() => setScale(prev => Math.min(3, prev + 0.1))} className="flex items-center gap-1 px-3 py-1 bg-neutral-700 rounded hover:bg-neutral-600">
                            <ZoomIn className="w-4 h-4" />
                            Zoom In
                        </button>
                        <button onClick={downloadSvgAsPng} className="flex items-center gap-1 px-3 py-1 bg-indigo-700 rounded hover:bg-indigo-600">
                            <Download className="w-4 h-4" />
                            Save PNG
                        </button>
                        <button onClick={toggleFullScreen} className="flex items-center gap-1 px-3 py-1 bg-neutral-700 rounded hover:bg-neutral-600">
                            {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                            {isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        </button>
                    </div>

                    {/* Diagram */}
                    <div className="overflow-hidden border border-neutral-800 rounded bg-neutral-800 relative">
                        <div
                            ref={renderRef}
                            className="p-4 cursor-grab active:cursor-grabbing"
                            style={{
                                overflow: 'auto',
                                maxHeight: '75vh',
                                minWidth: '600px',
                            }}
                            onMouseDown={(e) => {
                                const target = e.currentTarget;
                                let isDragging = false;
                                let startX = e.pageX - target.offsetLeft;
                                let startY = e.pageY - target.offsetTop;
                                const scrollLeft = target.scrollLeft;
                                const scrollTop = target.scrollTop;

                                const onMouseMove = (moveEvent: MouseEvent) => {
                                    isDragging = true;
                                    const x = moveEvent.pageX - target.offsetLeft;
                                    const y = moveEvent.pageY - target.offsetTop;
                                    target.scrollLeft = scrollLeft - (x - startX);
                                    target.scrollTop = scrollTop - (y - startY);
                                };

                                const onMouseUp = () => {
                                    document.removeEventListener('mousemove', onMouseMove);
                                    document.removeEventListener('mouseup', onMouseUp);
                                    if (isDragging) target.style.cursor = 'grab';
                                };

                                document.addEventListener('mousemove', onMouseMove);
                                document.addEventListener('mouseup', onMouseUp);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ERDPreviewModal;
